//Imports
import React, { useEffect, useMemo, useState } from "react";
import { LogManagerConsole } from 'Core/logManager';
import { useSelector } from 'react-redux';
import moment from 'moment';

//Controls
import { TimeFrame, useTimeFrameComponent, SidePanel, SidePanelHeader, SidePanelBody, SidePanelFooter, Label, PillsGroup } from "@gpstrackit/gtc-react-controls";

//Components
import TagsManage from 'Modules/.common/components/TagsManage';
import { Grid } from '@mui/material';
import { Button, Pill } from 'Components';

//Hooks
import { useIntl } from "react-intl";

const EmissionCheckAdvancedFilters = ({ open, preferences, toggle, changeFilters, hasFilters, statuses, scanStatuses }) => {
    //Redux
    const user = useSelector((state) => state.securityRedux.user);

    //Language
    const { messages, locale } = useIntl();
    // local states
    const defaultFrame = "custom";
    const [filters, setFilters] = useState({
        timeFrame: defaultFrame,
        status: [{ id: -1 }],
        scanStatus: [{ id: -1 }],
        tags: []
    });
    const [customDate, setCustomDate] = useState({})

    //const dispatch = useDispatch();
    const timeFrameOptions = useMemo(() => [
        { id: "today", label: messages['date_last_today'] },
        { id: "tomorrow", label: messages['date_next_tomorrow'] },
        { id: "next7days", label: messages['date_next_7days'] },
        { id: "next15days", label: messages['date_next_15days'] },
        { id: "nextMonth", label: messages['date_this_nextMonth'] },
        { id: "custom", label: messages['date_custom'] }
    ], [locale]);

    const timeFrame = useTimeFrameComponent(timeFrameOptions, defaultFrame);

    //Effects
    useEffect(() => {
        //filters
        LogManagerConsole("EmissionCheck", "AdvFilters", preferences);
        if (preferences?.filters) {
            timeFrame.defaultvalue(preferences?.filters?.nextDeadline?.option || defaultFrame);
            setCustomDate({})

            if (preferences?.filters?.nextDeadline?.option == 'custom') {
                setCustomDate({
                    startDate: moment(preferences?.filters?.nextDeadline?.startDate),
                    endDate: moment(preferences?.filters?.nextDeadline?.endDate)
                })
            }

            setFilters({
                status: preferences?.filters?.status || [{ id: -1 }],
                scanStatus: preferences?.filters?.scanStatus || [{ id: -1 }],
                timeFrame: preferences?.filters?.nextDeadline?.option || defaultFrame,
                tags: preferences?.filters?.tags || []
            })
        }
    }, [preferences])

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => toggle((prevValue) => !prevValue)}
            style={{ width: '350px', transform: 'translateX(350px)' }}
            classSidePanel={hasFilters ? "emissionCheck-advanced-filters-available" : ""}
        >
            <SidePanelHeader>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>{messages['emissionCheck_advancedFilters']}</h2>
                    </Grid>

                    <Grid item className='text-right'>
                        <Button
                            type='icon'
                            icon='delete'
                            tooltip={messages['emissionCheck_clearFilters']}
                            placement='left'
                            onClick={() => {
                                setFilters(prevFilters => ({ ...prevFilters, status: [{ id: - 1 }], scanStatus: [{ id: - 1 }], timeFrame: defaultFrame, tags: [] }))
                                setCustomDate({})
                                changeFilters({ filters: {} });
                                timeFrame.defaultvalue('today');
                                setTimeout(() => timeFrame.defaultvalue(defaultFrame), 25);
                            }}
                        />
                    </Grid>
                </Grid>
            </SidePanelHeader>
            <SidePanelBody>
                {open && <>
                    <Grid>
                        <Label>{messages['emissionCheck_nextDeadline']}</Label>
                        <div className="time-frame-container">
                            <TimeFrame
                                {...timeFrame}
                                {...customDate}
                                placeholder={messages['emissionCheck_nextDeadline']}
                                customRangeDate={90}
                                filterSwitch={false}
                                withTime={true}
                                minDate={moment().subtract(90, 'd')}
                                dateFormat={user?.dateformat || "MM/DD/YYYY hh:mm A"}
                                openDateRangePickerModalAutomatically={false}
                            />
                        </div>
                    </Grid>

                    <Grid>
                        <Label style={{ padding: '30px 0 3px 0', display: 'block' }}>{messages['emissionCheck_status']}</Label>
                        <div>
                            <PillsGroup
                                options={statuses}
                                onChange={(items) => {
                                    setFilters(prevFilters => ({ ...prevFilters, status: items.map(item => item) }))
                                }}
                                values={filters?.status?.length ? filters.status?.filter((x) => (x)) : [{ id: -1 }]}
                                multiple
                                multicolor
                            />
                        </div>
                    </Grid>

                    <Grid>
                        <Label style={{ padding: '30px 0 3px 0', display: 'block' }}>{messages['emissionCheck_lastScanStatus']}</Label>
                        <div>
                            <PillsGroup
                                options={scanStatuses}
                                onChange={(items) => {
                                    setFilters(prevFilters => ({ ...prevFilters, scanStatus: items.map(item => item) }))
                                }}
                                values={filters?.scanStatus?.length ? filters.scanStatus?.filter((x) => (x)) : [{ id: -1 }]}
                                style={{paddingRight: '5px'}}
                                multiple
                                multicolor
                            />
                        </div>
                    </Grid>

                    <Grid>
                        <Label style={{ paddingTop: '30px', display: 'block' }}>{messages['emissionCheck_tags']}</Label>
                        <div>
                            <TagsManage
                                onlyTags={true}
                                enableSelectAll={true}
                                mode="list"
                                height={300}
                                value={filters.tags || []}
                                onChange={(items) => setFilters(prevFilters => ({ ...prevFilters, tags: items }))}
                            />
                        </div>
                    </Grid>
                </>}
            </SidePanelBody>

            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        className="btn-cancel"
                        onClick={() => { toggle(false) }}
                    >
                        {messages["cancel"]}
                    </Button>

                    <Button
                        className="btn-blue"
                        onClick={() => {
                            let _filters = {}

                            // Next Deadline
                            if (timeFrame?.startDate) {
                                _filters.nextDeadline = {
                                    "option": timeFrame.option?.id,
                                    "startDate": timeFrame.startDate,
                                    "endDate": timeFrame.endDate
                                }
                            }

                            // Status
                            if (filters.status && filters.status != 'ALL') {
                                _filters.status = filters.status
                            }

                            // Scan Status
                            if (filters.scanStatus && filters.scanStatus != 'ALL') {
                                _filters.scanStatus = filters.scanStatus
                            }

                            // Tags
                            if (filters.tags.length) {
                                _filters.tags = filters.tags
                            }

                            toggle(false);
                            changeFilters({ filters: _filters });
                        }}
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    );
};

export default EmissionCheckAdvancedFilters;